import { FileText, Image as ImageIcon, Loader2, ScanSearch } from "lucide-react";
import { PROGRAM_TYPES } from "./constants";

function fmtDate(v, withYear4) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = withYear4 ? d.getFullYear() : String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

function Datum({ label, children, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm py-1 border-b border-[#1A1F2C]/5">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[#6B6358]">{label}</span>
      <span data-testid={testid} className="text-[#1A1F2C] text-right">{children}</span>
    </div>
  );
}

// Phase 2 — Green / Yellow / Red brand-compliance box, derived server-side from
// the AI inspection findings (only rules the AI could evaluate are "relevant").
const COMPLIANCE_META = {
  green: { color: "#1F6B6B", label: "Brand-compliant" },
  yellow: { color: "#C28A2E", label: "Needs review" },
  red: { color: "#B5462F", label: "Non-compliant" },
  inspecting: { color: "#C28A2E", label: "Evaluating…" },
  pending: { color: "#9A9A9A", label: "Not yet evaluated" },
  unavailable: { color: "#C28A2E", label: "Review unavailable" },
};

function ComplianceBox({ p, onClick }) {
  const c = p.compliance || { state: "pending" };
  let meta = COMPLIANCE_META[c.state] || COMPLIANCE_META.pending;
  let label = meta.label;
  if (c.state === "green" && (c.relevant_count || 0) === 0) label = "No applicable rules";
  const evaluated = ["green", "yellow", "red"].includes(c.state);
  const fmt = (n) => (evaluated ? (n ?? 0) : "—");
  return (
    <button
      type="button"
      data-testid={`brand-programs-row-${p.program_id}-compliance`}
      onClick={onClick}
      className="block w-full text-left border bg-[#1A1F2C]/[0.03] rounded-lg p-3 mb-3 hover:border-[#1A1F2C]/30 transition-colors"
      style={{ borderColor: `${meta.color}55` }}
      title="View per-rule compliance"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.16em] text-[#6B6358]">Brand-compliance status</span>
        {c.state === "inspecting"
          ? <Loader2 size={13} className="animate-spin" style={{ color: meta.color }} />
          : <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: meta.color }} />}
      </div>
      <p data-testid={`brand-programs-row-${p.program_id}-compliance-label`} className="text-sm mt-1 font-medium" style={{ color: evaluated ? meta.color : "#6B6358" }}>{label}</p>
      <div className="grid grid-cols-3 gap-2 mt-2 text-center">
        {[["Relevant rules", fmt(c.relevant_count)], ["Non-compliant", fmt(c.non_compliant_count)], ["Overrides", fmt(c.override_count)]].map(([l, v], i) => (
          <div key={i}>
            <div className="text-lg font-medium text-[#1A1F2C]">{v}</div>
            <div className="text-[9px] uppercase tracking-[0.12em] text-[#6B6358]">{l}</div>
          </div>
        ))}
      </div>
    </button>
  );
}

export default function ProgramCard({ p, canManage, onOpenSample, onChooseFile, onViewRules, onOpenAccess, onPublish, onArchive, onReactivate, onEdit, onDerive }) {
  const archived = p.lifecycle_status === "retired";
  const typeLabel = (PROGRAM_TYPES.find((t) => t.id === p.program_type) || {}).label || p.program_type;
  const sample = p.creative_sample;
  const thumb = sample?.thumbnail_url || sample?.page_image || p.derived_layout?.page_image;
  const layoutSaved = p.derived_layout?.status === "saved";
  const templateReady = !!p.template_id;
  const accessN = (p.policies?.eligible_location_ids || []).length
    + (p.policies?.restricted_user_ids || []).length
    + (p.policies?.restricted_roles || []).length;

  return (
    <div
      data-testid={`brand-programs-row-${p.program_id}`}
      className={`border rounded-xl p-5 ${archived ? "border-[#1A1F2C]/10 bg-[#1A1F2C]/[0.03]" : "border-[#1A1F2C]/10 bg-white"}`}
    >
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded-full bg-[#1F6B6B]/10 text-[#1F6B6B]">{typeLabel}</span>
        <h3 className="text-xl font-medium text-[#1A1F2C]">{p.name}</h3>
        <span
          data-testid={`brand-programs-row-${p.program_id}-status`}
          className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] rounded-full ${p.published ? "bg-[#1F6B6B] text-white" : "bg-[#6B6358]/15 text-[#6B6358]"}`}
        >
          {p.published ? "Published" : "Unpublished"}
        </span>
        {layoutSaved && <span data-testid={`brand-programs-row-${p.program_id}-layout-saved`} className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] rounded-full bg-[#1F6B6B]/12 text-[#1F6B6B]">Layout saved</span>}
        {templateReady && <span data-testid={`brand-programs-row-${p.program_id}-template-ready`} className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] rounded-full bg-[#C28A2E]/15 text-[#7a5a16]">Template ready</span>}
        {archived && <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] rounded-full bg-[#C28A2E]/15 text-[#7a5a16]">Archived</span>}
      </div>

      <div className="grid md:grid-cols-[200px_1fr] gap-5">
        {/* Thumbnail */}
        <div>
          {sample?.pdf_url ? (
            <>
              <button
                data-testid={`brand-programs-row-${p.program_id}-thumb`}
                onClick={onOpenSample}
                className="block w-full h-[150px] border border-[#1A1F2C]/15 rounded-lg overflow-hidden bg-white hover:border-[#1A1F2C]/40 transition-colors"
              >
                {thumb ? (
                  <img src={thumb} alt="Creative sample" className="w-full h-full object-cover object-top" />
                ) : (
                  <span className="w-full h-full flex flex-col items-center justify-center text-[#6B6358] gap-1">
                    <FileText size={26} />
                    <span className="text-[10px] uppercase tracking-[0.14em]">Open sample</span>
                  </span>
                )}
              </button>
              <button
                onClick={onOpenSample}
                data-testid={`brand-programs-row-${p.program_id}-sample`}
                className="block mt-1.5 text-xs text-[#1F6B6B] hover:underline truncate max-w-full text-left"
              >
                {sample.filename || "creative-sample.pdf"}
              </button>
            </>
          ) : (
            <div>
              <div
                data-testid={`brand-programs-row-${p.program_id}-no-sample`}
                className="w-full h-[150px] border border-dashed border-[#1A1F2C]/20 rounded-lg bg-[#1A1F2C]/[0.04] flex flex-col items-center justify-center text-center gap-1 px-3"
              >
                <ImageIcon size={22} className="text-[#6B6358]" />
                <span className="text-[11px] text-[#6B6358]">Creative sample required</span>
              </div>
              {canManage && (
                <button
                  data-testid={`brand-programs-row-${p.program_id}-choose-file`}
                  onClick={onChooseFile}
                  className="mt-2 w-full px-3 py-1.5 text-xs uppercase tracking-[0.16em] border border-[#1A1F2C]/30 rounded-full text-[#1A1F2C]"
                >
                  Choose File
                </button>
              )}
            </div>
          )}
        </div>

        {/* Compliance + datapoints */}
        <div>
          <ComplianceBox p={p} onClick={onViewRules} />

          <Datum label="Date Created" testid={`brand-programs-row-${p.program_id}-created`}>{fmtDate(p.created_at)}</Datum>
          <Datum label="Date First Published">{fmtDate(p.first_published_at)}</Datum>
          <Datum label="Most Recent Publish">{fmtDate(p.last_published_at, true)}</Datum>
          <Datum label="Current Status">{p.published ? "Published" : "Unpublished"}</Datum>
          <Datum label="Executions to Date" testid={`brand-programs-row-${p.program_id}-executions`}>{p.executions_count ?? 0}</Datum>
          <Datum label="Access Restrictions">
            {accessN > 0 ? (
              <button
                data-testid={`brand-programs-row-${p.program_id}-access-link`}
                onClick={onOpenAccess}
                className="text-[#1F6B6B] hover:underline"
              >
                {accessN}
              </button>
            ) : (
              <span data-testid={`brand-programs-row-${p.program_id}-access-zero`} className="text-[#6B6358]">0</span>
            )}
          </Datum>
        </div>
      </div>

      {canManage && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#1A1F2C]/10">
          <button
            data-testid={`brand-programs-row-${p.program_id}-view-rules`}
            onClick={onViewRules}
            className="px-4 py-2 text-xs uppercase tracking-[0.16em] border border-[#1A1F2C]/30 rounded-full text-[#1A1F2C]"
          >
            View / Override Brand Rules
          </button>
          {sample?.pdf_url && (
            <button
              data-testid={`brand-programs-row-${p.program_id}-derive`}
              onClick={onDerive}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-[0.16em] border border-[#1A1F2C]/30 rounded-full text-[#1A1F2C]"
            >
              <ScanSearch size={13} /> {layoutSaved ? "Edit layout" : "Detect layout"}
            </button>
          )}
          {!archived && (
            <button
              data-testid={`brand-programs-row-${p.program_id}-publish`}
              onClick={onPublish}
              className={`px-4 py-2 text-xs uppercase tracking-[0.16em] rounded-full ${p.published ? "border border-[#C28A2E]/50 text-[#C28A2E]" : "bg-[#1F6B6B] text-white"}`}
            >
              {p.published ? "Unpublish" : "Publish"}
            </button>
          )}
          {archived ? (
            <button
              data-testid={`brand-programs-row-${p.program_id}-reactivate`}
              onClick={onReactivate}
              className="px-4 py-2 text-xs uppercase tracking-[0.16em] border border-[#1F6B6B]/50 rounded-full text-[#1F6B6B]"
            >
              Reactivate
            </button>
          ) : (
            <button
              data-testid={`brand-programs-row-${p.program_id}-archive`}
              onClick={onArchive}
              className="px-4 py-2 text-xs uppercase tracking-[0.16em] border border-[#B5462F]/40 rounded-full text-[#B5462F]"
            >
              Archive
            </button>
          )}
          <button
            onClick={onEdit}
            className="ml-auto px-4 py-2 text-xs uppercase tracking-[0.16em] text-[#6B6358]"
          >
            Edit details
          </button>
        </div>
      )}
    </div>
  );
}
